import { Component, computed, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { LogService } from '../log.service';
import { LogSearchParams } from './log-search-params.model';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { LogSearchMaskinerService } from './log-search-maskiner.service';
import { LogSearchSystemerService } from './log-search-systemer.service';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { LogSearchMetoderService } from './log-search-metoder.service';
import { ScrollPanel } from 'primeng/scrollpanel';
import { LetInputCprComponent } from '@shared/let-input-cpr/let-input-cpr.component';

@Component({
  selector: 'app-log-search',
  imports: [
    CheckboxModule,
    ReactiveFormsModule,
    SelectModule,
    InputGroup,
    InputGroupAddonModule,
    InputText,
    Message,
    DatePicker,
    Button,
    ScrollPanel,
    LetInputCprComponent,
  ],
  templateUrl: './log-search.component.html',
  styleUrl: './log-search.component.css',
})
export class LogSearchComponent {
  logService = inject(LogService);
  data = this.logService.datasource.data;

  maskinerService = inject(LogSearchMaskinerService);
  systemerService = inject(LogSearchSystemerService);
  metoderService = inject(LogSearchMetoderService);

  itcOptions = ['BD', 'BEC', 'SDC'];

  periodeOptions = [
    { text: 'Sidste 20 minutter', value: 'sidste20minutter' },
    { text: 'Sidste time', value: 'sidsteTime' },
    { text: 'I dag', value: 'idag' },
    { text: 'I går', value: 'igaar' },
    { text: 'Sidste 7 dage', value: 'sidste7dage' },
    { text: 'Sidste 30 dage', value: 'sidste30dage' },
  ];

  systemOptions = computed(() => {
    const list = this.systemerService.data();
    //Vi vil gerne have SSB øverst, da den bruges mest
    return ['SSB', ...list.filter(x => x !== 'SSB')];
  });

  form = new FormGroup({
    startTidspunktDate: new FormControl(
      dayjs().subtract(20, 'minute').toDate()
    ),
    slutTidspunktDate: new FormControl(dayjs().add(5, 'minute').toDate()),

    periode: new FormControl(),
    fejlet: new FormControl(),
    requestId: new FormControl(),
    sessionId: new FormControl(),
    regnr: new FormControl(),
    itCentral: new FormControl(),
    cpr: new FormControl(),
    raadgiverId: new FormControl(),
    brugernavn: new FormControl(),
    tilbudId: new FormControl(),
    tilbudNavn: new FormControl(),

    maskine: new FormControl(),
    system: new FormControl(),
    metode: new FormControl(),
    fritekst: new FormControl(),
    tidsforbrug: new FormControl(),
  });

  resetPeriode() {
    this.form.patchValue({ periode: null });
  }

  onPeriodeChange(e: SelectChangeEvent) {
    const p = e.value;

    if (p == 'sidste20minutter') {
      this.form.patchValue({
        startTidspunktDate: dayjs().subtract(20, 'minute').toDate(),
        slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
      });
    }
    if (p == 'sidsteTime') {
      this.form.patchValue({
        startTidspunktDate: dayjs().subtract(1, 'hour').toDate(),
        slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
      });
    }
    if (p == 'sidste7dage') {
      this.form.patchValue({
        startTidspunktDate: dayjs().subtract(1, 'week').startOf('day').toDate(),
        slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
      });
    }
    if (p == 'sidste30dage') {
      this.form.patchValue({
        startTidspunktDate: dayjs().subtract(30, 'day').startOf('day').toDate(),
        slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
      });
    }
    if (p == 'idag') {
      this.form.patchValue({
        startTidspunktDate: dayjs().startOf('day').toDate(),
        slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
      });
    }
    if (p == 'igaar') {
      this.form.patchValue({
        startTidspunktDate: dayjs().startOf('day').subtract(1, 'day').toDate(),
        slutTidspunktDate: dayjs()
          .startOf('day')
          .subtract(1, 'second')
          .toDate(),
      });
    }
  }

  async onClickSearch() {
    await this.logService.searchLog({
      ...(this.form.value as LogSearchParams),
      startTidspunkt: dayjs(this.form.value.startTidspunktDate).format(
        'YYYY-MM-DD HH:mm'
      ),
      slutTidspunkt: dayjs(this.form.value.slutTidspunktDate).format(
        'YYYY-MM-DD HH:mm'
      ),
    });
  }

  onClickReset() {
    this.form.reset();
    this.form.patchValue({
      startTidspunktDate: dayjs().subtract(20, 'minute').toDate(),
      slutTidspunktDate: dayjs().add(5, 'minute').toDate(),
    });
  }

  noHits = computed(() => {
    return (
      this.logService.datasource.isLoaded() && this.data()?.totalRowCount == 0
    );
  });
}
